import { useState } from 'react';

import { TextField, Box } from '@mui/material';
import { useTranslate } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import type { DTMFInputData } from '../dataProvider/types';

export const getMaxDigitsValidationMessage = (maxDigits: number): string => {
  if (maxDigits < 1 || maxDigits > 128 || isNaN(maxDigits)) {
    flowchartLogger.debug('Validation: Max digits is invalid', maxDigits);
    return 'callflows.nodes.playback.maxDigitsErrorInvalid';
  }
  flowchartLogger.debug('Validation: Max digits is valid', maxDigits);
  return '';
};

export const getOutputPropNameValidationMessage = (outputPropName: string): string => {
  const reg = new RegExp(outputPropNameRegex);
  if (outputPropName === '') {
    flowchartLogger.debug('Validation: Output prop name is empty');
    return 'callflows.nodes.playback.outputPropNameErrorEmpty';
  }
  if (!reg.test(outputPropName)) {
    flowchartLogger.debug('Validation: Output prop name is invalid', outputPropName);
    return 'callflows.nodes.playback.outputPropNameErrorInvalid';
  }
  flowchartLogger.debug('Validation: Output prop name is valid', outputPropName);
  return '';
};

export const getTerminatorsValidationMessage = (terminators: string): string => {
  const reg = new RegExp(terminatorsRegex);
  if (terminators === '' || !reg.test(terminators)) {
    flowchartLogger.debug('Validation: Terminators are invalid', terminators);
    return 'callflows.nodes.playback.terminatorsErrorInvalid';
  }
  flowchartLogger.debug('Validation: Terminators are valid', terminators);
  return '';
};

export const getTimeoutValidationMessage = (timeout: number): string => {
  if (timeout < 500 || timeout > 60000 || isNaN(timeout)) {
    flowchartLogger.debug('Validation: Timeout is invalid', timeout);
    return 'callflows.nodes.playback.timeoutErrorInvalid';
  }
  flowchartLogger.debug('Validation: Timeout is valid', timeout);
  return '';
};

interface DTMFInputProps extends DTMFInputData {
  onUpdate: (value: { maxDigits: number; outputPropName: string; terminators: string; timeout: number }) => void;
}

const outputPropNameRegex = '^[a-zA-Z][a-zA-Z0-9_]{2,}$';
const terminatorsRegex = '^[0-9#*]+$';

const DTMFInput = ({
  maxDigits,
  outputPropName,
  terminators,
  timeout,
  onUpdate,
}: DTMFInputProps): React.ReactElement => {
  const translate = useTranslate();
  const [maxDigitsHelperText, setMaxDigitsHelperText] = useState<string>('');
  const [outputPropNameHelperText, setOutputPropNameHelperText] = useState<string>('');
  const [terminatorsHelperText, setTerminatorsHelperText] = useState<string>('');
  const [timeoutHelperText, setTimeoutHelperText] = useState<string>('');

  const handleMaxDigitsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    let maxDigits = parseInt(event.target.value);
    if (isNaN(maxDigits)) {
      maxDigits = 1;
    }
    setMaxDigitsHelperText(translate(getMaxDigitsValidationMessage(maxDigits)));
    onUpdate({ maxDigits, outputPropName, terminators, timeout });
  };

  const handleOutputPropNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const outputPropName = event.target.value;
    setOutputPropNameHelperText(translate(getOutputPropNameValidationMessage(outputPropName)));
    onUpdate({ maxDigits, outputPropName, terminators, timeout });
  };

  const handleTerminatorsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const terminators = event.target.value;
    setTerminatorsHelperText(translate(getTerminatorsValidationMessage(terminators)));
    onUpdate({ maxDigits, outputPropName, terminators, timeout });
  };

  const handleTimeoutChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    let timeout = parseInt(event.target.value);
    if (isNaN(timeout)) {
      timeout = 500;
    }
    setTimeoutHelperText(translate(getTimeoutValidationMessage(timeout)));
    onUpdate({ maxDigits, outputPropName, terminators, timeout });
  };

  return (
    <Box data-testid='dtmf-input' display='flex' gap={1}>
      <TextField
        sx={{ my: 1 }}
        label={`${translate('callflows.nodes.playback.maxDigits')}`}
        onChange={handleMaxDigitsChange}
        type='number'
        value={maxDigits}
        error={maxDigitsHelperText !== ''}
        helperText={maxDigitsHelperText}
        slotProps={{ htmlInput: { min: 1, max: 128 } }}
      />
      <TextField
        sx={{ my: 1 }}
        label={`${translate('callflows.nodes.playback.outputPropName')}`}
        onChange={handleOutputPropNameChange}
        value={outputPropName}
        error={outputPropNameHelperText !== ''}
        helperText={outputPropNameHelperText}
        required
      />
      <TextField
        sx={{ my: 1 }}
        label={`${translate('callflows.nodes.playback.terminators')}`}
        onChange={handleTerminatorsChange}
        value={terminators}
        error={terminatorsHelperText !== ''}
        helperText={terminatorsHelperText}
      />
      <TextField
        sx={{ my: 1 }}
        label={`${translate('callflows.nodes.playback.timeout')}`}
        onChange={handleTimeoutChange}
        type='number'
        value={timeout}
        error={timeoutHelperText !== ''}
        helperText={timeoutHelperText}
        slotProps={{ htmlInput: { min: 500, max: 60000 } }}
      />
    </Box>
  );
};

export default DTMFInput;
